import { useEffect, useMemo, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdInput, TdNumber, TdSeamless } from '../../../../components/inline-add-table/cells';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { usePermission } from '../../../../auth/AuthContext';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { awardCategoriesApi, nominationsApi } from '../praiseApi';
import type { AwardCategoryDetail, AwardCriterionInput, AwardDetail } from '../praiseTypes';
import { sampleAward, sampleCriteria } from '../utils/samplePraise';

const ID_PREFIX = 'award';

/** The server's `PraiseFieldRules.MaxWinners` — the cap is enforced there; this only explains it. */
const MAX_WINNERS = 50;

/** `maxPoints` to `max-points` — exactly as `focusValidationError` does it. */
const kebab = (field: string) => field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

/** The id of one criterion cell — a CONTRACT with the server, which names a rejected line `criteria.2.maxPoints`. */
const cellId = (index: number, field: string) => `${ID_PREFIX}-criteria.${index}.${kebab(field)}`;

/** A document criterion's draft — its points and note, keyed by the document. Its NAME is the document's. */
interface DocumentDraft {
  maxPoints: number | null;
  description: string;
}

/** One of the award's OWN criteria, as drafted. */
interface OwnDraft {
  key: string;
  /** The criterion's own id when it is already on the award. */
  id: string | null;
  name: string;
  description: string;
  maxPoints: number | null;
}

const EMPTY_OWN = (): OwnDraft => ({ key: uid(), id: null, name: '', description: '', maxPoints: null });

const isBlankOwn = (l: OwnDraft) => l.name.trim() === '' && l.description.trim() === '' && l.maxPoints == null;

const isFilledOwn = (l: OwnDraft) => l.name.trim() !== '' && l.maxPoints != null && l.maxPoints >= 1;

/** What the modal writes. A CREATE writes the award and its criteria in one save; on a record each section has its own. */
export type AwardSection = 'create' | 'details' | 'criteria';

/**
 * Open an award for nomination, or edit ONE section of it — Details or Criteria.
 *
 * **Every document requirement is a criterion** (product call, 31 Oct 2026): choosing the category draws
 * its documents as the first criteria rows, named by the document, and the reader gives each the most
 * points it can earn. The award's OWN criteria follow, added like any inline row. **Criteria are fixed
 * once scoring starts**, which the profile's Criteria card says before offering this form.
 *
 * **On a Details edit the category is STATED, not offered** — every nominee was asked for its documents.
 */
export function AwardModal({
  open,
  section,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  section: AwardSection;
  /** The record a section edit works on. Null on a create. */
  editing: AwardDetail | null;
  onClose: () => void;
  /** `keepOpen` after a create with Add another on — the parent refreshes and leaves the form up. */
  onSaved: (saved: AwardDetail, options?: { keepOpen?: boolean }) => void;
}) {
  const toast = useToast();
  const creating = section === 'create';
  const showDetails = section !== 'criteria';
  const showCriteria = section !== 'details';

  const [categoryId, setCategoryId] = useState<string | null>(editing?.awardCategoryId ?? null);
  const [description, setDescription] = useState(editing?.description ?? '');
  // A string while typed, parsed below — the whole-number field idiom; a half-typed value is not a number yet.
  const [winnerText, setWinnerText] = useState(String(editing?.winnerCount ?? 1));
  const winnerCount = /^\d+$/.test(winnerText.trim()) ? Number(winnerText.trim()) : null;
  const winnersValid = winnerCount != null && winnerCount >= 1 && winnerCount <= MAX_WINNERS;
  // The deliberation named winners against this number, so it is fixed from then on — disabled, saying why.
  const winnersFixed = editing?.deliberatedAt != null;

  const [documentDrafts, setDocumentDrafts] = useState<Record<string, DocumentDraft>>(() =>
    Object.fromEntries(
      (editing?.criteria ?? [])
        .filter((c) => c.awardCategoryDocumentId != null)
        .map((c) => [c.awardCategoryDocumentId as string, { maxPoints: c.maxPoints, description: c.description ?? '' }]),
    ),
  );
  const [own, setOwn] = useState<OwnDraft[]>(() => {
    const kept = (editing?.criteria ?? [])
      .filter((c) => c.awardCategoryDocumentId == null)
      .map((c) => ({ key: uid(), id: c.id, name: c.name, description: c.description ?? '', maxPoints: c.maxPoints }));

    return kept.length > 0 ? kept : [EMPTY_OWN()];
  });

  const [categories, setCategories] = useState<AwardCategoryDetail[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(creating);
  const [categoriesFailed, setCategoriesFailed] = useState(false);

  const [addAnother, setAddAnother] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open || !creating) return;

    const controller = new AbortController();

    awardCategoriesApi
      .options(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setCategories(rows);
      })
      // A failed feed names no field, so it stays out of the save path — but it is SAID.
      .catch(() => {
        if (!controller.signal.aborted) setCategoriesFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCategories(false);
      });

    return () => controller.abort();
  }, [open, creating]);

  const usable = useMemo(() => categories.filter((c) => c.documents.length > 0), [categories]);
  const heldBack = categories.length - usable.length;

  const categoryOptions = useMemo(
    () =>
      editing !== null
        ? [{ value: editing.awardCategoryId, label: editing.categoryName }]
        : usable.map((c) => ({
            value: c.id,
            label: c.name,
            sublabel: `${c.documents.length} ${c.documents.length === 1 ? 'document' : 'documents'}`,
          })),
    [editing, usable],
  );

  /*
    THE DOCUMENT ROWS, derived during render: on a create, the chosen category's checklist; on a
    Criteria edit, the document criteria the award already has. Their drafts are keyed by document,
    so switching category and back keeps what was typed.
  */
  const documents = useMemo(
    () =>
      creating
        ? (usable.find((c) => c.id === categoryId)?.documents ?? []).map((d) => ({
            id: d.id,
            name: d.name,
            defaultMaxPoints: d.defaultMaxPoints ?? null,
          }))
        : (editing?.criteria ?? [])
            .filter((c) => c.awardCategoryDocumentId != null)
            .map((c) => ({ id: c.awardCategoryDocumentId as string, name: c.name, defaultMaxPoints: null as number | null })),
    [creating, usable, categoryId, editing],
  );

  /*
    A document the reader has not touched starts at its CATEGORY's default points — derived during
    render, so choosing another category re-derives it, and a typed value always wins.
  */
  const draftOf = (documentId: string): DocumentDraft =>
    documentDrafts[documentId] ?? {
      maxPoints: documents.find((d) => d.id === documentId)?.defaultMaxPoints ?? null,
      description: '',
    };

  // The write-back moves a CATEGORY row, so it happens only for an editor holding that key — said on the form.
  const updatesCategory = usePermission('award-categories.update');

  const documentsValid = documents.every((d) => (draftOf(d.id).maxPoints ?? 0) >= 1);
  const ownValid = own.every((l) => isFilledOwn(l) || isBlankOwn(l));
  const total =
    documents.reduce((sum, d) => sum + (draftOf(d.id).maxPoints ?? 0), 0) +
    own.filter(isFilledOwn).reduce((sum, l) => sum + (l.maxPoints ?? 0), 0);
  const criteriaCount = documents.length + own.filter(isFilledOwn).length;

  const detailsValid = categoryId != null && winnersValid;
  const criteriaValid = documents.length > 0 && documentsValid && ownValid;
  const valid = (!showDetails || detailsValid) && (!showCriteria || criteriaValid);
  const canSave = valid && confirmed && !saving;

  const saveDisabledReason = saving
    ? null
    : showDetails && categoryId == null
      ? 'Choose the category.'
      : showDetails && !winnersValid
        ? `Give the number of winners, 1 to ${MAX_WINNERS}.`
      : showCriteria && !documentsValid
        ? 'Give every document requirement the most points it can earn.'
        : showCriteria && !ownValid
          ? 'Every criterion needs a name and its points. Remove the unfinished one, or fill it in.'
          : !confirmed
            ? 'Tick the confirm box to submit.'
            : null;

  function patchDocument(documentId: string, change: Partial<DocumentDraft>) {
    setDocumentDrafts((current) => ({ ...current, [documentId]: { ...draftOf(documentId), ...change } }));
  }

  function patchOwn(key: string, change: Partial<OwnDraft>) {
    setOwn((current) => current.map((l) => (l.key === key ? { ...l, ...change } : l)));
  }

  // DOCUMENTS FIRST, then the award's own — the server's order, so a rejection's index is the row on screen.
  const criteriaBody = (): AwardCriterionInput[] => [
    ...documents.map((d) => ({
      id: null,
      awardCategoryDocumentId: d.id,
      name: null,
      description: draftOf(d.id).description.trim() || null,
      maxPoints: draftOf(d.id).maxPoints,
    })),
    ...own.map((l) => ({
      id: l.id,
      awardCategoryDocumentId: null,
      name: l.name.trim() || null,
      description: l.description.trim() || null,
      maxPoints: l.maxPoints,
    })),
  ];

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const details = { awardCategoryId: categoryId, description: description.trim() || null, winnerCount };

    try {
      const saved =
        section === 'create'
          ? await nominationsApi.create({ ...details, criteria: criteriaBody() })
          : section === 'details'
            ? await nominationsApi.update(editing!.id, details)
            : await nominationsApi.updateCriteria(editing!.id, criteriaBody());

      toast.success(
        section === 'create'
          ? `${saved.categoryName} was opened for nomination.`
          : section === 'details'
            ? `${saved.categoryName} was updated.`
            : `${saved.categoryName}'s criteria were updated.`,
      );

      if (creating && addAnother) {
        // The CATEGORY is the placement and stays, with its points; the description is this award's own.
        setDescription('');
        setConfirmed(false);
        onSaved(saved, { keepOpen: true });
      } else {
        onSaved(saved);
      }
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this award.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: ID_PREFIX,
        // The refusals naming the WHOLE list — a document left without points, the cap — are answered at the `+`.
        fieldIds: { criteria: `${ID_PREFIX}-add-criterion` },
      });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    // A category the reader already chose is decided; the generator picks one only where none is.
    let target = categoryId;

    if (showDetails) {
      const sample = sampleAward(categoryOptions);

      setDescription(sample.description);
      if (!winnersFixed) setWinnerText(String(sample.winnerCount));
      if (creating && target == null) {
        target = sample.awardCategoryId;
        setCategoryId(target);
      }
    }

    if (showCriteria) {
      // The documents of the category just settled — read off the live list, since this render's rows predate it.
      const documentIds = creating
        ? (usable.find((c) => c.id === target)?.documents.map((d) => d.id) ?? [])
        : documents.map((d) => d.id);
      const sample = sampleCriteria(documentIds);

      setDocumentDrafts((current) => ({ ...current, ...sample.documents }));
      setOwn(sample.own.map((l) => ({ key: uid(), id: null, ...l })));
    }
  }

  const heading =
    section === 'create' ? 'New award' : section === 'details' ? `Edit ${editing!.referenceNumber}` : `Edit criteria — ${editing!.categoryName}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={heading}
      size={showCriteria ? '2xl' : 'xl'}
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving || loadingCategories }}
      addAnother={
        creating ? { id: `${ID_PREFIX}-add-another`, checked: addAnother, onChange: setAddAnother, disabled: saving } : undefined
      }
      confirm={{ id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {categoriesFailed ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">The categories could not be loaded. Close this form and open it again.</p>
          </div>
        ) : null}

        {showDetails ? (
          <>
            {/*
              ONE FIELD PER ROW (product call, 31 Oct 2026), Attrition's form shape. There is no Title
              (03 Nov 2026): the CATEGORY is what the award is called.
            */}
            <div className="flex flex-col gap-4">
              <Select
                id={`${ID_PREFIX}-award-category-id`}
                label="Category"
                required
                description={
                  !creating
                    ? 'Fixed once the award is created — every nominee was asked for this category’s documents. Delete the award and open a new one to change it.'
                    : heldBack > 0
                      ? `The kind of award. ${heldBack} ${heldBack === 1 ? 'category has' : 'categories have'} no documents on ${heldBack === 1 ? 'its' : 'their'} checklist yet and ${heldBack === 1 ? 'is' : 'are'} not offered — see PRAISE → Settings.`
                      : 'The kind of award. Its documents become the first criteria, and every nominee is asked for them.'
                }
                value={categoryId}
                onChange={(next) => setCategoryId(next || null)}
                options={categoryOptions}
                loading={loadingCategories}
                placeholder="Choose a category"
                remote
                sort="none"
                error={categoryId != null ? error?.fieldError('awardCategoryId') : 'Choose the category.'}
                disabled={saving || !creating}
              />

              <TextField
                id={`${ID_PREFIX}-winner-count`}
                label="Number of winners"
                required
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_WINNERS}
                className="font-mono"
                description={
                  winnersFixed
                    ? 'Fixed once the deliberation is recorded — it named its winners against this number.'
                    : 'How many winners the deliberation may name. 1 for a single winner; more for a top 3, a top 5.'
                }
                value={winnerText}
                onChange={(e) => setWinnerText(e.target.value)}
                error={
                  winnerText.trim() !== '' && !winnersValid
                    ? `Give a whole number between 1 and ${MAX_WINNERS}.`
                    : error?.fieldError('winnerCount')
                }
                disabled={saving || winnersFixed}
              />
            </div>

            <Textarea
              id={`${ID_PREFIX}-description`}
              label="Description"
              description="Anything nominators and evaluators should know about this award — who may be nominated, how it is judged."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={error?.fieldError('description')}
              disabled={saving}
            />
          </>
        ) : null}

        {showCriteria ? (
          <>
            <FormFieldset legend="Criteria">
              <InlineAddTable
                headers={['Criterion *', { label: 'Max points *', align: 'right' }, 'Description']}
                colWidths={[260, 130, 300]}
                resizeKey={`${ID_PREFIX}-criteria-v1`}
                flexColumn={2}
                addButtonId={`${ID_PREFIX}-add-criterion`}
                addDisabled={saving || own.some((l) => !isFilledOwn(l))}
                addDisabledReason="Finish the current criterion before adding another."
                rowCount={documents.length + own.length}
                onAddRow={() => setOwn((current) => [...current, EMPTY_OWN()])}
                // The running TOTAL — the Disbursement Request grid's footer shape.
                footerContent={
                  <div className="flex items-center justify-end gap-3 pr-1">
                    <span className="text-xs text-slate-500">
                      Total points · {criteriaCount} {criteriaCount === 1 ? 'criterion' : 'criteria'}
                    </span>
                    <span id={`${ID_PREFIX}-total`} className="font-mono text-sm font-medium tabular-nums text-slate-800">
                      {total}
                    </span>
                  </div>
                }
              >
                {documents.map((document, index) => (
                  <InlineRow
                    key={document.id}
                    idx={index + 1}
                    onRemove={() => undefined}
                    // A document row cannot be taken off — it is a requirement every nominee is asked for.
                    menu={{
                      id: `${ID_PREFIX}-criteria-${index}-menu`,
                      items: [
                        {
                          key: 'remove',
                          label: 'Remove',
                          disabledReason: 'A document requirement is always a criterion. Change it on the category’s checklist.',
                          onClick: () => undefined,
                        },
                      ],
                    }}
                  >
                    <TdSeamless below={<span className="text-xs text-slate-400">Document requirement</span>}>
                      {document.name}
                    </TdSeamless>
                    <TdNumber
                      id={cellId(index, 'maxPoints')}
                      ariaLabel={`Max points, ${document.name}`}
                      value={draftOf(document.id).maxPoints}
                      onChange={(next) => patchDocument(document.id, { maxPoints: next })}
                      allowNull
                      integerOnly
                      min={0}
                      disabled={saving}
                    />
                    <TdInput
                      id={cellId(index, 'description')}
                      ariaLabel={`Description, ${document.name}`}
                      value={draftOf(document.id).description}
                      onChange={(next) => patchDocument(document.id, { description: next })}
                      placeholder="What an evaluator looks for"
                      disabled={saving}
                    />
                  </InlineRow>
                ))}

                {own.map((line, ownIndex) => {
                  const index = documents.length + ownIndex;

                  return (
                    <InlineRow
                      key={line.key}
                      idx={index + 1}
                      onRemove={() => setOwn((current) => current.filter((l) => l.key !== line.key))}
                    >
                      <TdInput
                        id={cellId(index, 'name')}
                        ariaLabel={`Criterion, line ${index + 1}`}
                        value={line.name}
                        onChange={(next) => patchOwn(line.key, { name: next })}
                        placeholder="Impact on service"
                        disabled={saving}
                      />
                      <TdNumber
                        id={cellId(index, 'maxPoints')}
                        ariaLabel={`Max points, line ${index + 1}`}
                        value={line.maxPoints}
                        onChange={(next) => patchOwn(line.key, { maxPoints: next })}
                        allowNull
                        integerOnly
                        min={0}
                        disabled={saving}
                      />
                      <TdInput
                        id={cellId(index, 'description')}
                        ariaLabel={`Description, line ${index + 1}`}
                        value={line.description}
                        onChange={(next) => patchOwn(line.key, { description: next })}
                        disabled={saving}
                      />
                    </InlineRow>
                  );
                })}
              </InlineAddTable>

              {error?.fieldError('criteria') ? <p className="text-xs text-red-700">{error.fieldError('criteria')}</p> : null}
            </FormFieldset>

            {/* The CONSEQUENCES, read before the tick. */}
            <p className="text-xs text-slate-500">
              {documents.length === 0
                ? 'Choose the category first — its document requirements become the first criteria.'
                : `The total is the most a nominee can score. Each evaluator scores every criterion from 0 to its maximum, and a nominee's result is the average of the evaluators' totals. The criteria are fixed once the first score is saved. ${
                    updatesCategory
                      ? "Document points you set here also become the category's default for the next award; awards already created keep their own."
                      : "Document points you set here apply to this award only — changing the category's defaults needs the Award Categories update permission."
                  }`}
            </p>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
